import type { Query, QueryKey } from "@tanstack/react-query";

/**
 * The query key of an instance-scoped read: the request path, then the instance it was read in.
 *
 * The instance rides in a header, not in the path, so a key made of the path alone is the same
 * key in every instance: an id-less key is cross-instance state (HANDOFF 2026-10-05). A switch
 * in place resets the cache and remounts the shell (`TenantInstanceContext`), and this key is the
 * second line: a read made in one instance can never be served to another.
 *
 * The second element is an object, not the bare id, so these keys stay apart from the Overview's
 * `[path, instanceId]` entries, which have their own freshness (`hooks/use-overview.ts`).
 * `invalidateQueries({ queryKey: [path] })` still reaches every one of them by prefix.
 */
export function instanceKey(path: string, instanceId: string | null | undefined) {
  return [path, { instance: instanceId ?? null }] as const;
}

function isInstanceKey(key: QueryKey): boolean {
  const scope = key[1];
  return key.length === 2 && typeof scope === "object" && scope !== null && "instance" in scope;
}

/**
 * Filter for the instance-scoped list under `path`, in whichever instance the page is in, and
 * for no other entry (the Overview's, a detail's). The replacement for `{ queryKey: [path],
 * exact: true }` now that the key carries the instance.
 */
export function instanceListFilter(path: string): {
  queryKey: QueryKey;
  predicate: (query: Query) => boolean;
} {
  return { queryKey: [path], predicate: (query) => isInstanceKey(query.queryKey) };
}
